import React, { createContext, useCallback, useContext, useState } from 'react';
import { ConsumerLocale } from './types/ConsumerLocale';
import { getSupportedLanguages } from './api/translate';

interface TranslationState {
	languageFrom?: string;
	languageTo?: string;
	isTextAfterRtl: boolean;
	isTextBeforeRtl: boolean;
	initialTextToTranslate: string;
	textBefore: string;
	textAfter: string;
	languages: ConsumerLocale[];
	isDetectLanguageLoading: boolean;
	autoDetectLanguageEnabled: boolean;
	autoTranslate: boolean | null;
	isSettingsOpen: boolean;
}

interface TranslationActions {
	setLanguageTo: (languageTo?: string) => void;
	setLanguageFrom: (languageFrom?: string) => void;
	setIsTextAfterRtl: (isRtl: boolean) => void;
	setIsTextBeforeRtl: (isRtl: boolean) => void;
	setTextBefore: (text: string) => void;
	setInitialTextToTranslate: (text: string) => void;
	setTextAfter: (text: string) => void;
	initLanguages: (auth0IdToken: string | null, consumerId: string | null) => Promise<ConsumerLocale[] | []>;
	resetCachedSelectedLanguages: () => void
	setIsDetectLanguageLoading: (isDetectLanguageLoading: boolean) => void;
	setAutoDetectLanguageEnabled: (autoDetectLanguageEnabled: boolean) => void;
	setAutoTranslate: (autoTranslate: boolean) => void;
	setIsSettingsOpen: (isSettingsOpen: boolean) => void;
}

interface TranslationContextType extends TranslationState, TranslationActions {
}

interface TranslationProviderProps {
	children: React.ReactNode;
}

const initialState: TranslationState = {
	languageFrom: localStorage.getItem('languageFrom') ?? '',
	languageTo: localStorage.getItem('languageTo') ?? '',
	isTextAfterRtl: false,
	isTextBeforeRtl: false,
	initialTextToTranslate: '',
	textBefore: '',
	textAfter: '',
	languages: [],
	isDetectLanguageLoading: false,
	autoDetectLanguageEnabled: true,
	autoTranslate: null,
	isSettingsOpen: false,
};

const TranslationContext = createContext<TranslationContextType | null>(null);

export const useTranslationData = () => {
	const context = useContext(TranslationContext);
	if (!context) {
		throw new Error('useTranslationData must be used within a TranslationProvider');
	}
	return context;
};

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
	// State
	const [state, setState] = useState<TranslationState>(initialState);

	// Actions
	const setLanguageFrom = useCallback((languageFrom?: string) => {
		setState(prev => ({ ...prev, languageFrom }));
		localStorage.setItem(`languageFrom`, languageFrom ?? '');
	}, []);

	const setLanguageTo = useCallback((languageTo?: string) => {
		setState(prev => ({ ...prev, languageTo }));
		localStorage.setItem(`languageTo`, languageTo ?? '');
	}, []);

	const setIsTextAfterRtl = useCallback((isTextAfterRtl: boolean) => {
		setState(prev => ({ ...prev, isTextAfterRtl }));
	}, []);

	const setIsTextBeforeRtl = useCallback((isTextBeforeRtl: boolean) => {
		setState(prev => ({ ...prev, isTextBeforeRtl }));
	}, []);

	const setTextBefore = useCallback((textBefore: string) => {
		setState(prev => ({ ...prev, textBefore }));
	}, []);

	const setInitialTextToTranslate = useCallback((initialTextToTranslate: string) => {
		setState(prev => ({ ...prev, initialTextToTranslate }));
	}, []);

	const setTextAfter = useCallback((textAfter: string) => {
		setState(prev => ({ ...prev, textAfter }));
	}, []);

	const initLanguages = useCallback(async (auth0Token: string | null, consumerId: string | null): Promise<ConsumerLocale[] | []> => {
		try {
			const languages = await getSupportedLanguages(auth0Token, consumerId);
			setState(prev => ({ ...prev, languages }));
			return languages;
		} catch (error) {
			console.error('Failed to initialize languages:', error);
			setState(prev => ({ ...prev, languages: [] }));
			return [];
		}
	}, []);

	const setIsDetectLanguageLoading = useCallback((isDetectLanguageLoading: boolean) => {
		setState(prev => ({ ...prev, isDetectLanguageLoading }));
	}, []);

	const setAutoDetectLanguageEnabled = useCallback((autoDetectLanguageEnabled: boolean) => {
		setState(prev => ({ ...prev, autoDetectLanguageEnabled }));
	}, []);

	const setAutoTranslate = useCallback((autoTranslate: boolean) => {
		setState(prev => ({ ...prev, autoTranslate }));
	}, []);

	const setIsSettingsOpen = useCallback((isSettingsOpen: boolean) => {
		setState(prev => ({ ...prev, isSettingsOpen: isSettingsOpen }));
	}, []);

	const resetCachedSelectedLanguages = () => {
		localStorage.removeItem('languageFrom');
		localStorage.removeItem('languageTo');
		setLanguageFrom('');
		setLanguageTo('');
	};

	const value: TranslationContextType = {
		...state,
		setLanguageFrom,
		setLanguageTo,
		setIsTextAfterRtl,
		setIsTextBeforeRtl,
		setTextBefore,
		setTextAfter,
		initLanguages,
		resetCachedSelectedLanguages,
		setIsDetectLanguageLoading,
		setAutoDetectLanguageEnabled,
		setAutoTranslate,
		setInitialTextToTranslate,
		setIsSettingsOpen
	};

	return (
		<TranslationContext.Provider value={value}>
			{children}
		</TranslationContext.Provider>
	);
};